import BigNumber from 'bignumber.js'
import { useTranslation } from 'contexts/Localization'
import { useMemo } from 'react'
import { DeserializedFarm } from 'state/types'
import { useFarms, usePollFarmsWithUserData, usePriceVvsUsdc, useTokenPricesUsdc } from 'state/farms/hooks'
import { getAddress } from 'utils/addressHelpers'
import { getFarmAprByVersion } from 'utils/apr'
import { getBalanceAmount, getDisplayUserLiquidity } from 'utils/formatBalance'
import { Skeleton } from 'vvs-uikit'
import { getLpUsdcPrice } from 'utils/farmsPriceHelpers'
import { getTotalApr, getTotalBoostedApr } from 'views/Farms/utils'
import { useGetBoostedFarms } from 'hooks/boost-farm/useFarmBoostContract'
import { Option, TokenPairSelector } from '.'

type Options = {
  boostMultiplier: number
  filterFn?: (farm: DeserializedFarm) => boolean
}

export const useTokenPairOptions = (opts?: Options) => {
  const { boostMultiplier = 1, filterFn } = opts || {}
  usePollFarmsWithUserData()
  const vvsPrice = usePriceVvsUsdc()
  const { data: boostedFarms } = useGetBoostedFarms()
  const { t } = useTranslation()
  const { data: farmsLP, userDataLoaded } = useFarms()
  const tokenPrices = useTokenPricesUsdc(farmsLP)
  const enableBoostFarms = farmsLP.filter(
    (farm) =>
      farm.pid !== 0 &&
      !farm.isFinished &&
      ((farm.enableBoost && !farm.boostFarmExpired) || boostedFarms?.includes(farm.pid)),
  )
  const activeFarms = filterFn ? enableBoostFarms.filter(filterFn) : enableBoostFarms
  const pairOptions = useMemo(() => {
    return activeFarms.map((farm) => {
      const { stakedBalance } = farm?.userData

      const stakedBalanceBigNumber = getBalanceAmount(stakedBalance)

      const displayUserLiquidity = () => {
        if (stakedBalanceBigNumber.gt(0)) {
          return getDisplayUserLiquidity(stakedBalanceBigNumber)
        }
        return (
          <TokenPairSelector.WarnMsg>
            {stakedBalanceBigNumber.toFixed(3, BigNumber.ROUND_DOWN)}
          </TokenPairSelector.WarnMsg>
        )
      }

      const totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)
      const lpTokenPrice: BigNumber = getLpUsdcPrice(farm)

      const stakedLiquidityInUSD =
        farm.version === 2 && farm.migration
          ? getBalanceAmount(farm.lpTokenAmountInCraftsmanV2).times(lpTokenPrice)
          : totalLiquidity

      const { vvsRewardsApr, tokenRewardsApr, lpRewardsApr } = getFarmAprByVersion(
        farm.poolWeight,
        vvsPrice,
        totalLiquidity,
        getAddress(farm.lpAddresses),
        farm.version,
        farm.rewardData,
        stakedLiquidityInUSD,
        tokenPrices,
      )

      return {
        id: farm.pid,
        tokenPair: {
          primary: farm.token,
          secondary: farm.quoteToken,
          label: farm.lpSymbol.split(' ')[0],
        },
        vvsRewardsApr,
        tokenRewardsApr,
        lpRewardsApr,
        version: farm.version,
        lpToken: getAddress(farm.lpAddresses),
        details: [
          {
            label: t('Your Staked Liquidity'),
            value: userDataLoaded && stakedBalanceBigNumber ? displayUserLiquidity() : <Skeleton />,
          },
          {
            label: boostMultiplier ? (
              t('Max Boost APR (up to %boostMultiplier%x)', { boostMultiplier: boostMultiplier.toFixed(1) })
            ) : (
              <Skeleton />
            ),
            value:
              tokenRewardsApr > 0 && boostMultiplier ? (
                <TokenPairSelector.DetailBoostApr
                  from={getTotalApr(tokenRewardsApr, lpRewardsApr) / 100}
                  to={getTotalBoostedApr(vvsRewardsApr, tokenRewardsApr, lpRewardsApr, boostMultiplier) / 100}
                />
              ) : (
                <Skeleton />
              ),
          },
        ],
      }
    }) as Option[]
  }, [activeFarms, t, userDataLoaded, boostMultiplier, vvsPrice, tokenPrices])

  return {
    pairOptions,
    isLoading: !userDataLoaded,
  }
}
